import { Children, isValidElement, ReactNode } from "react";

/**
 * Extracts a human-readable string from React children for use as a `data-tour` attribute.
 *
 * - String/number children are returned directly.
 * - Arrays are recursed and joined.
 * - React elements: checks `props.title` first (e.g. CollapseHeader), then recurses into `props.children`.
 * - The result is sanitised: trimmed, lowercased, whitespace → hyphens, non-alphanumeric stripped, truncated to 50 chars.
 *
 * Returns an empty string when nothing extractable is found.
 * Callers should use `value || undefined` to avoid rendering an empty `data-tour=""`.
 */
export function getDataTourFromChildren(node: ReactNode): string {
  const raw = extractText(node);
  return sanitize(raw);
}

function extractText(node: ReactNode): string {
  if (node == null || typeof node === "boolean") {
    return "";
  }

  if (typeof node === "string") {
    return node;
  }

  if (typeof node === "number") {
    return String(node);
  }

  if (Array.isArray(node)) {
    return Children.toArray(node).map(extractText).join(" ");
  }

  if (isValidElement(node)) {
    const { title, children } = (node.props ?? {}) as Record<string, unknown>;
    if (typeof title === "string" && title.length > 0) {
      return title;
    }
    return extractText(children as ReactNode);
  }

  return "";
}

function sanitize(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .slice(0, 50);
}
